import { theme } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { useToastContext } from '../../../contexts/ToastProvider';
import bookingsAPI from '../../../services/api/bookings';
import useStore from '../../../store';
import { LandlordBooking, Status } from '../../../types/bookings';
import Modal from '../../Modal/Modal';
import ModalBody from '../../Modal/ModalBody/ModalBody';
import ModalContent from '../../Modal/ModalContent/ModalContent';
import ModalFooter from '../../Modal/ModalFooter/ModalFooter';
import ModalHeader from '../../Modal/ModalHeader/ModalHeader';
import {
  StyleButtonWrapper,
  StyledBodyText,
  StyledButton,
  StyledSpinner,
} from './BookingAcceptModal.styles';

const BookingAcceptModal = ({
  onClose,
  booking,
  setBookings,
}: {
  onClose: () => void;
  booking: LandlordBooking;
  setBookings: Dispatch<SetStateAction<LandlordBooking[]>>;
}) => {
  const { t } = useTranslation(['booking', 'common', 'error']);
  const toast = useToastContext();
  const { user } = useStore(state => ({
    user: state.user,
  }));

  const { mutateAsync, isLoading } = useMutation(
    bookingsAPI.acceptLandLordBooking,
    {
      onSuccess: () => {
        toast.success(upperFirst(t('booking:acceptBookingSuccess')));
        setBookings(prevValue => {
          const newBooking = [...prevValue];
          const bookingIndex = newBooking.findIndex(
            preValBooking => preValBooking.id === booking.id,
          );
          newBooking.splice(bookingIndex, 1, {
            ...booking,
            status: Status.ACCEPTED,
          });
          return newBooking;
        });
        onClose();
      },
      onError: () => {
        toast.error(upperFirst(t('error:error')));
      },
    },
  );

  return (
    <Modal>
      <ModalBody>
        <ModalHeader
          title={upperFirst(t('booking:confirmAccept'))}
          onClose={onClose}
        />
        <ModalContent>
          <StyledBodyText>
            {upperFirst(t('booking:acceptBooking'))}
          </StyledBodyText>
        </ModalContent>
        <ModalFooter>
          <StyleButtonWrapper>
            <StyledButton
              user={user?.type}
              variant="secondary"
              onClick={onClose}
            >
              {upperFirst(t('common:cancel'))}
            </StyledButton>
            <StyledButton
              user={user?.type}
              onClick={() => mutateAsync({ bookingId: booking.id })}
              disabled={isLoading}
            >
              {isLoading && (
                <StyledSpinner
                  foregroundColor={theme.colors.cross.ultralightGray}
                  size={15}
                  strokeSize={2}
                />
              )}
              {upperFirst(t('common:accept'))}
            </StyledButton>
          </StyleButtonWrapper>
        </ModalFooter>
      </ModalBody>
    </Modal>
  );
};

export default BookingAcceptModal;
